import { Common } from 'src/AppState/Common';
import { UniverseType } from 'src_common/common/universe';
import { CasinoDataMapper } from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/CasinoDataMapper';
import {
    ApiProviderType,
    CollectionType,
    LegacyCategoriesAndGamesType,
    LegacyCreateCategoryResponseType,
    LegacyDeleteCategoryResponseType,
    LegacyEditCategoryParamsType,
    LegacyEditCategoryResponseType,
} from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/types';
import { apiGetCategoriesAndGamesRequest } from 'src/api_openapi/v2/casino-store/apiGetCategoriesAndGames';
import { apiCreateCategoryRequest } from 'src/api_openapi/v2/casino-store/apiCreateCategory';
import { apiRemoveCategoryRequest } from 'src/api_openapi/v2/casino-store/apiRemoveCategory';
import { apiEditCategoryRequest } from 'src/api_openapi/v2/casino-store/apiEditCategory';
import { apiChangeCategoryPositionRequest } from 'src/api_openapi/v2/casino-store/apiChangeCategoryPosition';
import { apiAssignGameToCategoryRequest } from 'src/api_openapi/v2/casino-store/apiAssignGameToCategory';
import { apiChangeGameOrderRequest } from 'src/api_openapi/v2/casino-store/apiChangeGameOrder';
import { OpenapiCategoriesAndGamesStaffEditGameResponse200Type } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_game';
import { LegacyEditGameParamsType } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { apiEditLiveGameRequest } from 'src/api_openapi/v2/casino-store/apiEditLiveGame';
import { apiChangeLiveGameAssignmentRequest } from 'src/api_openapi/v2/casino-store/apiChangeLiveGameAssignment';
import { apiDeleteLiveGameRequest } from 'src/api_openapi/v2/casino-store/apiDeleteLiveGame';

export class CasinoStoreApiProvider implements ApiProviderType {
    private readonly universe: UniverseType;
    private readonly gatewayUrl: string;
    private readonly jwt: string;

    public constructor(common: Common) {
        this.universe = common.appEnvParams.universe_api;
        this.gatewayUrl = common.appEnvParams.gatewayUrl;
        this.jwt = common.session.currentJwt;
    }

    public async getCategoriesAndGames(collection: CollectionType): Promise<LegacyCategoriesAndGamesType> {
        const response = await apiGetCategoriesAndGamesRequest(
            this.gatewayUrl,
            { collection_slug: collection, status: 'all' },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to get categories and games for collection ${collection}`);
        }

        return {
            categories: response.body.categories.map((category) =>
                CasinoDataMapper.toLegacyCategoryFormat(category, this.universe)
            ),
            games: response.body.games.map((game) => CasinoDataMapper.toLegacyGameFormat(game, this.universe)),
        };
    }

    public async createCategory(
        position: number,
        name: string,
        collection: CollectionType
    ): Promise<LegacyCreateCategoryResponseType> {
        const response = await apiCreateCategoryRequest(
            this.gatewayUrl,
            {
                collection_slug: collection,
                requestBody: {
                    position,
                    name,
                },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to create category with position ${position} and name ${name}`);
        }

        return CasinoDataMapper.toLegacyCreateCategoryResponseFormat(response.body.createdCategory, this.universe);
    }

    public async removeCategory(
        categoryId: number,
        _collection: CollectionType
    ): Promise<LegacyDeleteCategoryResponseType> {
        const response = await apiRemoveCategoryRequest(
            this.gatewayUrl,
            { category_id: categoryId },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to remove category with ID ${categoryId}`);
        }

        return CasinoDataMapper.toLegacyDeleteCategoryResponseFormat(response.body.rowsAffected);
    }

    public async editCategory(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        _collection: CollectionType
    ): Promise<LegacyEditCategoryResponseType> {
        const response = await apiEditCategoryRequest(
            this.gatewayUrl,
            {
                category_id: categoryId,
                requestBody: {
                    categoryDetails: {
                        name: categoryDetails.name,
                        active: categoryDetails.active,
                        visibleInNavigation: categoryDetails.visible_in_navigation,
                        visibleInAllGames: categoryDetails.visible_in_all_games,
                    },
                },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to edit category with ID ${categoryId}`);
        }

        return CasinoDataMapper.toLegacyEditCategoryResponseFormat(response.body.modifiedCategory);
    }

    public async changeCategoryPosition(
        _newOrder: number[],
        updatedCategory: { categoryId: number; newPosition: number },
        _collection: CollectionType
    ): Promise<void> {
        const { categoryId, newPosition } = updatedCategory;

        const response = await apiChangeCategoryPositionRequest(
            this.gatewayUrl,
            {
                category_id: categoryId,
                requestBody: { newPosition },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to change position for category with ID ${categoryId}`);
        }
    }

    public async assignGameToCategory(gameId: number, categoryId: number, position: number): Promise<void> {
        const response = await apiAssignGameToCategoryRequest(
            this.gatewayUrl,
            {
                game_id: gameId,
                category_id: categoryId,
                requestBody: { position },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to assign game with ID ${gameId} to category with ID ${categoryId}`);
        }
    }

    public async deleteGameFromCategory(
        gameId: number,
        categoryIds: Array<string>,
        _collection: CollectionType
    ): Promise<void> {
        const response = await apiChangeLiveGameAssignmentRequest(
            this.gatewayUrl,
            {
                game_id: gameId,
                requestBody: {
                    categoryIds: categoryIds.map((id) => parseInt(id, 10)),
                },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to delete game with ID ${gameId}`);
        }
    }

    public async deleteLiveGame(gameId: number): Promise<void> {
        const response = await apiDeleteLiveGameRequest(
            this.gatewayUrl,
            {
                game_id: gameId,
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to delete live game with ID ${gameId}`);
        }
    }

    public async changeGamesOrder(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        _collection: CollectionType
    ): Promise<void> {
        const response = await apiChangeGameOrderRequest(
            this.gatewayUrl,
            {
                category_id: categoryId,
                requestBody: { gameIds: categoryDetails.game_ids },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to change games order for category with ID ${categoryId}`);
        }
    }

    public editGame = async (
        game: LegacyEditGameParamsType
    ): Promise<OpenapiCategoriesAndGamesStaffEditGameResponse200Type> => {
        const gameDetails = game.gameDetails;
        const response = await apiEditLiveGameRequest(
            this.gatewayUrl,
            {
                game_id: game.gameId,
                requestBody: {
                    liveGameDetails: CasinoDataMapper.toCasinoStoreGameFormat(gameDetails),
                },
            },
            {
                authorization: `Bearer ${this.jwt}`,
            }
        );

        if (response.status !== 200) {
            throw Error(`Failed to edit game with ID ${game.gameId}`);
        }

        return CasinoDataMapper.toLegacyEditGameResponseFormat();
    };
}
